import { color, cssVar } from '@/design-system';
import { isReadable, mostReadable, parseColor } from '@/shared/lib';

import type {
    MailDeliveryResource,
    MailSettingsResource,
    TenantBrandingResource,
    TenantDomainResource,
    TenantResource,
    TenantSettingsResource,
} from './types';

/**
 * De eigen omgeving zoals de schermen haar zien: het bedrijf, de huisstijl, de
 * instellingen, de eigen mailserver en de eigen domeinen. De `to*`-functies zijn
 * de enige plek waar de lijnvorm uit `types.ts` wordt vertaald.
 */

/* -------------------------------------------------------------------------- */
/* Het bedrijf                                                                 */
/* -------------------------------------------------------------------------- */

/** Het adres van het bedrijf zoals het bewaard is; wat er niet is, is `null`. */
export interface TenantAddress {
    street: string | null;
    number: string | null;
    suffix: string | null;
    postcode: string | null;
    city: string | null;
}

/**
 * De proef van een bedrijf dat nog niet betaalt. Na een aanmelding op de website
 * heeft hij geen einddatum: hij is op als de gratis keuringen op zijn.
 */
export interface TenantTrial {
    /** ISO-8601, of `null`: geen tijdslimiet. */
    endsAt: string | null;
    /** De gratis keuringen die nog over zijn; `null` voor wie het tegoed niet mag zien. */
    inspectionsRemaining: number | null;
}

export interface Tenant {
    uuid: string;
    /** De handelsnaam: boven berichten aan klanten, op het rapport en op de klantpagina. */
    name: string;
    slug: string;
    /** De statutaire naam op de factuur en in de NHG-bijlage; `null` is de handelsnaam. */
    legalName: string | null;
    kvkNumber: string | null;
    vatNumber: string | null;
    /** Het rekeningnummer op de factuur, zonder spaties. */
    iban: string | null;
    email: string | null;
    phone: string | null;
    website: string | null;
    address: TenantAddress;
    logoUrl: string | null;
    planName: string | null;
    /** `null` voor een bedrijf dat niet (meer) in de proef is. */
    trial: TenantTrial | null;
    /** De keuze op de prijzenlijst van de website: `start`, `pro`, `business` of `single`. */
    planPreference: string | null;
}

export function toTenant(resource: TenantResource): Tenant {
    return {
        uuid: resource.uuid,
        name: resource.name,
        slug: resource.slug,
        legalName: resource.legal_name,
        kvkNumber: resource.kvk_number,
        vatNumber: resource.vat_number,
        iban: resource.iban,
        email: resource.email,
        phone: resource.phone,
        website: resource.website,
        address: {
            street: resource.address.street,
            number: resource.address.number,
            suffix: resource.address.suffix,
            postcode: resource.address.postcode,
            city: resource.address.city,
        },
        logoUrl: resource.logo_url,
        planName: resource.plan?.name ?? null,
        trial: resource.trial
            ? {
                  endsAt: resource.trial.ends_at,
                  inspectionsRemaining: resource.trial.inspections_remaining,
              }
            : null,
        planPreference: resource.plan_preference ?? null,
    };
}

/**
 * Wat je bij **Bedrijfsgegevens** opslaat (`PATCH /tenant`), zoals het in de velden
 * staat. Een leeg veld haalt het gegeven weg; de API schrijft nummers, website en
 * adres in de vorm waarin ze gedrukt worden.
 */
export interface CompanyDetails {
    name: string;
    legalName: string;
    kvkNumber: string;
    vatNumber: string;
    iban: string;
    email: string;
    phone: string;
    website: string;
    address: TenantAddress;
}

/** `NL91ABNA0417164300` → `NL91 ABNA 0417 1643 00`: een IBAN zoals een bank het schrijft. */
export function formatIban(iban: string | null): string {
    return (iban ?? '')
        .replace(/\s+/g, '')
        .toUpperCase()
        .replace(/(.{4})(?=.)/g, '$1 ');
}

/* -------------------------------------------------------------------------- */
/* Huisstijl                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Vier kleuren om mee te beginnen. Ze kleuren het **rapport, de e-mails en de
 * klantpagina**, nooit de app-chrome: de app blijft Smartkeur-groen
 * (patterns.md §10). De API bewaart de kleurcode; die komt uit de tokens, net
 * als de CSS-variabele waarmee het scherm hem tekent.
 *
 * Het zijn startpunten, geen keuzelijst: daarnaast staat een kleurkiezer met het
 * volledige spectrum en een veld voor een eigen HEX- of RGB-code.
 */
export const BRAND_ACCENTS = [
    {
        key: 'accent',
        label: 'Smartkeur-groen',
        cssVar: cssVar.swatchAccent,
        value: color.swatchAccent,
    },
    { key: 'blue', label: 'Blauw', cssVar: cssVar.swatchBlue, value: color.swatchBlue },
    { key: 'orange', label: 'Oranje', cssVar: cssVar.swatchOrange, value: color.swatchOrange },
    { key: 'slate', label: 'Antraciet', cssVar: cssVar.swatchSlate, value: color.swatchSlate },
] as const;

export type BrandAccentKey = (typeof BRAND_ACCENTS)[number]['key'];

/** Welke van de vier swatches deze kleurcode is; `null` voor een eigen kleur. */
export function brandAccentFor(value: string | null): BrandAccentKey | null {
    const normalised = value?.trim().toLowerCase();

    return BRAND_ACCENTS.find((swatch) => swatch.value.toLowerCase() === normalised)?.key ?? null;
}

/** Alleen een zescijferige kleurcode, zoals de API hem aanneemt en teruggeeft. */
export function isAccentColor(value: string | null | undefined): value is string {
    return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value.trim());
}

/**
 * De drie kleuren van de huisstijl, zoals ze bewaard zijn.
 *
 * Meer dan drie zou iets moeten betekenen, en de rest van het rapport is niet
 * per bedrijf gekleurd: de vier keuringsresultaten en de amber van "offline"
 * zeggen *wat iets is* en blijven bij elk bedrijf gelijk. Een rood dat groen
 * wordt omdat een klant van groen houdt, liegt over een huis.
 *
 * `accentText` en `heading` mogen `null` zijn: niet gekozen. De API kiest dan
 * zelf de leesbare tekstkleur, en koppen houden de inkt die het rapport altijd
 * al had.
 */
export interface HouseStyle {
    /** De kleur van het bedrijf: de regel boven een hoofdstuk, de knop, een link. */
    accent: string;
    /** De tekst *op* die accentkleur, of `null` voor "kies zelf wat leesbaar is". */
    accentText: string | null;
    /** De rustige tweede kleur voor koppen en balken, of `null` voor de eigen inkt. */
    heading: string | null;
}

/** Wat een bedrijf zonder eigen kleur krijgt; dezelfde blauwe als `HouseStyle::DEFAULT_ACCENT`. */
export const DEFAULT_ACCENT = color.swatchBlue;

/** Wit en inkt: de twee kandidaten voor tekst op een accentkleur. */
export const ON_ACCENT_LIGHT = color.textOnDark;
export const ON_ACCENT_DARK = color.textPrimary;

/** Koppen worden op wit papier gedrukt. */
export const HEADING_BACKGROUND = color.surfaceCard;

/**
 * De kleur die de tekst op het accent krijgt als je zelf niets koos: dezelfde
 * keuze die `HouseStyle::of()` in de API maakt.
 */
export function defaultTextOnAccent(accent: string): string {
    return mostReadable(accent, ON_ACCENT_LIGHT, ON_ACCENT_DARK);
}

/** De kleur die daadwerkelijk op het accent gedrukt wordt. */
export function effectiveTextOnAccent(style: HouseStyle): string {
    return style.accentText !== null && isReadable(style.accentText, style.accent)
        ? style.accentText
        : defaultTextOnAccent(style.accent);
}

export interface Branding {
    /** De kleurcode van het bedrijf, of `null` als die nog niet gekozen is. */
    accentColor: string | null;
    /** De swatch die bij `accentColor` hoort; `null` bij een eigen kleur. */
    accent: BrandAccentKey | null;
    /** De tekst op de accentkleur, of `null` als de API die zelf mag kiezen. */
    accentTextColor: string | null;
    /** De rustige tweede kleur voor koppen, of `null` voor de eigen inkt. */
    headingColor: string | null;
    logoUrl: string | null;
    reportFooterText: string | null;
}

export function toBranding(resource: TenantBrandingResource): Branding {
    const accentColor = isAccentColor(resource.accent_color) ? resource.accent_color : null;

    return {
        accentColor,
        accent: brandAccentFor(accentColor),
        accentTextColor: parseColor(resource.accent_text_color),
        headingColor: parseColor(resource.heading_color),
        logoUrl: resource.logo_url,
        reportFooterText: resource.report_footer_text,
    };
}

/** De huisstijl van een bewaarde branding, met het standaardaccent als er niets staat. */
export function toHouseStyle(branding: Branding): HouseStyle {
    return {
        accent: branding.accentColor ?? DEFAULT_ACCENT,
        accentText: branding.accentTextColor,
        heading: branding.headingColor,
    };
}

/**
 * Wat de API als logo aanneemt (`MediaCollection::Logo`). SVG hoort er bewust
 * niet bij: de API weigert elk bestand met actieve inhoud.
 */
export const LOGO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

export function isAcceptedLogo(file: Pick<File, 'type'>): boolean {
    return (LOGO_MIME_TYPES as readonly string[]).includes(file.type);
}

/* -------------------------------------------------------------------------- */
/* Instellingen: automatisering en rapportlinks                                */
/* -------------------------------------------------------------------------- */

/**
 * De instellingen die de API echt gebruikt, in de namen van de schermen.
 *
 * Alleen wat de API ook uitvoert: een schakelaar die niets doet, hoort niet in
 * beeld. `openingHours` staat hier (nog) niet, omdat geen scherm hem aanpast.
 */
export interface TenantSettings {
    /** Het rapport gaat naar de klant zodra het klaar is, zonder na te lezen. */
    autoSendReport: boolean;
    /** Na het versturen van het rapport ontstaat een conceptfactuur; uitgeven doet de eigenaar. */
    autoInvoice: boolean;
    /**
     * Gaat het rapport vanzelf naar de klant (`autoSendReport`), dan zit de factuur in
     * dezelfde mail, met het standaard eigen bericht: zo nodig gemaakt uit de diensten en
     * definitief gemaakt.
     */
    invoiceWithReport: boolean;
    /** De klant krijgt een herinnering aan de afspraak, per mail en (met nummer) per sms. */
    sendReminders: boolean;
    /** Zoveel dagen voor de afspraak, op het tijdstip van de afspraak; `0` is de ochtend zelf (0 tot 30). */
    reminderDaysBefore: number;
    /** Nieuwe keuringen weigeren zodra het tegoed (plus de coulance) op is. */
    blockOnEmptyCredits: boolean;
    /** Zoveel credits mag het saldo onder nul komen voordat blokkeren ingaat (0 tot 1000). */
    graceCredits: number;
    /** Vanaf dit saldo waarschuwt de app dat het tegoed bijna op is (0 tot 10000). */
    lowCreditThreshold: number;
    /** Standaard voor een nieuwe rapportlink. */
    showCostsOnPublicReport: boolean;
    allowForwarding: boolean;
    requireAccessCode: boolean;
    /** Hoe lang een nieuwe rapportlink werkt, in dagen (30 tot 3650). */
    reportLinkValidDays: number;
    /**
     * Het rapport van een bouwkundige keuring krijgt de NHG-bijlage, zolang de
     * vragenlijst die bijlage ook heeft.
     */
    includeNhgAppendix: boolean;
    /** Wat de bijlage aankruist bij de keuringsinstantie; `null` laat de vier vakjes leeg. */
    nhgRegisteredAs: NhgRegistration | null;
    /**
     * Het standaard eigen bericht in de mail die rapport en factuur samen brengt. De
     * verstuurdialoog begint ermee; per verzending pas je het aan. Leeg: geen bericht.
     */
    invoiceWithReportMessage: string;
}

/**
 * Wat het bedrijf zelf mag wijzigen: alles behalve de tegoedregels. Of nieuwe
 * keuringen stoppen als de keuringen op zijn en hoe ver het saldo onder nul mag,
 * zet alleen het platform (`PATCH /platform/tenants/{tenant}/credit-rules`); de
 * API weigert ze van het bedrijf met een 422.
 */
export type TenantSettingsPatch = Partial<
    Omit<TenantSettings, 'blockOnEmptyCredits' | 'graceCredits'>
>;

export function toTenantSettings(resource: TenantSettingsResource): TenantSettings {
    return {
        autoSendReport: resource.autoSendReport,
        autoInvoice: resource.autoInvoice,
        invoiceWithReport: resource.invoiceWithReport,
        sendReminders: resource.sendReminders,
        reminderDaysBefore: resource.reminderDaysBefore,
        blockOnEmptyCredits: resource.blockOnEmptyCredits,
        graceCredits: resource.graceCredits,
        lowCreditThreshold: resource.lowCreditThreshold,
        showCostsOnPublicReport: resource.showCostsOnPublicReport,
        allowForwarding: resource.allowForwarding,
        requireAccessCode: resource.requireAccessCode,
        reportLinkValidDays: resource.reportLinkValidDays,
        includeNhgAppendix: resource.includeNhgAppendix,
        nhgRegisteredAs: isNhgRegistration(resource.nhgRegisteredAs)
            ? resource.nhgRegisteredAs
            : null,
        invoiceWithReportMessage: resource.invoiceWithReportMessage ?? '',
    };
}

/**
 * Als wat een keuringsbedrijf in de NHG-bijlage staat ingeschreven: precies de
 * vier vakjes van het "Model bouwkundig rapport NHG" (`Tenancy\Enums\NhgRegistration`).
 */
export const NHG_REGISTRATIONS = [
    'schade_expertisebureau',
    'architectenbureau',
    'bouwkundig_adviesbureau',
    'aannemersbedrijf',
] as const;

export type NhgRegistration = (typeof NHG_REGISTRATIONS)[number];

/** Zoals het NHG-model ze drukt. */
export const NHG_REGISTRATION_LABELS: Record<NhgRegistration, string> = {
    schade_expertisebureau: '(Bouwkundig)schade expertisebureau',
    architectenbureau: 'Architectenbureau',
    bouwkundig_adviesbureau: 'Bouwkundig adviesbureau',
    aannemersbedrijf: 'Aannemersbedrijf',
};

export function isNhgRegistration(value: unknown): value is NhgRegistration {
    return (NHG_REGISTRATIONS as ReadonlyArray<unknown>).includes(value);
}

/** Zo lang mag het eigen bericht in de mail met rapport en factuur zijn, standaard en per verzending. */
export const INVOICE_MESSAGE_MAX = 2000;

/** De grenzen van het tegoedgedrag in de API: de ruimte onder nul (alleen het platform zet die) en de waarschuwing. */
export const GRACE_CREDITS_MAX = 1000;
export const LOW_CREDIT_THRESHOLD_MAX = 10000;

/** De keuzes voor het moment van een herinnering, in dagen voor de afspraak (de API staat 0 tot 30 toe). */
export const REMINDER_DAYS = [0, 1, 2, 3, 5, 7, 14] as const;

/** De grenzen van `reportLinkValidDays` in de API. */
export const REPORT_LINK_MIN_DAYS = 30;
export const REPORT_LINK_MAX_DAYS = 3650;

/** De keuzes voor de geldigheid van een rapportlink, in dagen. */
export const REPORT_LINK_DAYS = [30, 90, 180, 365, 730, 1095, 1825, 3650] as const;

/** `365` → `1 jaar`, `90` → `3 maanden`, `45` → `45 dagen`. */
export function linkValidityLabel(days: number): string {
    if (days % 365 === 0) {
        const years = days / 365;

        return years === 1 ? '1 jaar' : `${years} jaar`;
    }

    if (days % 30 === 0 && days < 365) {
        const months = days / 30;

        return months === 1 ? '1 maand' : `${months} maanden`;
    }

    return days === 1 ? '1 dag' : `${days} dagen`;
}

/**
 * Tot wanneer een link werkt die je nu deelt: vandaag plus de termijn, tot het
 * einde van die dag (`ShareSettings::fromTenant` in de API).
 */
export function linkValidUntil(days: number, now: Date = new Date()): Date {
    const until = new Date(now);

    until.setDate(until.getDate() + days);
    until.setHours(23, 59, 59, 0);

    return until;
}

/* -------------------------------------------------------------------------- */
/* Eigen mailserver                                                            */
/* -------------------------------------------------------------------------- */

export const MAIL_DRIVERS = ['platform', 'smtp', 'ses', 'postmark', 'lettermint'] as const;

export type MailDriver = (typeof MAIL_DRIVERS)[number];

/** Gelijk aan `MailDriver::label()` in de API. */
export const MAIL_DRIVER_LABELS: Record<MailDriver, string> = {
    platform: 'Verzenden via Smartkeur',
    smtp: 'Eigen SMTP-server',
    ses: 'Amazon SES',
    postmark: 'Postmark',
    lettermint: 'Lettermint',
};

export const MAIL_ENCRYPTIONS = ['tls', 'ssl', 'none'] as const;

export type MailEncryption = (typeof MAIL_ENCRYPTIONS)[number];

/** Gelijk aan `MailEncryption::label()` in de API. */
export const MAIL_ENCRYPTION_LABELS: Record<MailEncryption, string> = {
    tls: 'TLS (aanbevolen)',
    ssl: 'SSL',
    none: 'Geen',
};

function oneOf<T extends string>(values: readonly T[], value: string | null): T | null {
    return value !== null && (values as readonly string[]).includes(value) ? (value as T) : null;
}

export interface MailSettings {
    driver: MailDriver;
    host: string | null;
    port: number | null;
    username: string | null;
    encryption: MailEncryption | null;
    fromName: string | null;
    fromAddress: string | null;
    replyToAddress: string | null;
    /** Een geslaagd testbericht zet dit, en activeert daarmee de eigen afzender. */
    verifiedAt: string | null;
    lastError: string | null;
    /** Zolang dit `true` is, gaat mail uit onder de afzender van Smartkeur. */
    usesPlatformFallback: boolean;
}

export function toMailSettings(resource: MailSettingsResource): MailSettings {
    return {
        driver: oneOf(MAIL_DRIVERS, resource.driver) ?? 'platform',
        host: resource.host,
        port: resource.port,
        username: resource.username,
        encryption: oneOf(MAIL_ENCRYPTIONS, resource.encryption),
        fromName: resource.from_name,
        fromAddress: resource.from_address,
        replyToAddress: resource.reply_to_address,
        verifiedAt: resource.verified_at,
        lastError: resource.last_error,
        usesPlatformFallback: resource.uses_platform_fallback,
    };
}

/** Het wachtwoord gaat alleen mee als het opnieuw is ingetypt. */
export interface MailSettingsInput {
    driver: MailDriver;
    host: string | null;
    port: number | null;
    username: string | null;
    password?: string;
    encryption: MailEncryption | null;
    fromName: string | null;
    fromAddress: string | null;
    replyToAddress: string | null;
}

export interface MailTestResult {
    delivered: boolean;
    usedPlatformFallback: boolean;
    error: string | null;
}

export function toMailTestResult(resource: MailDeliveryResource): MailTestResult {
    return {
        delivered: resource.delivered,
        usedPlatformFallback: resource.used_platform_fallback,
        error: resource.error,
    };
}

/* -------------------------------------------------------------------------- */
/* Eigen domeinen                                                              */
/* -------------------------------------------------------------------------- */

export const DOMAIN_TYPES = ['report', 'app', 'api'] as const;

export type DomainType = (typeof DOMAIN_TYPES)[number];

/** Waarvoor een domein dient, in de woorden van het scherm. */
export const DOMAIN_TYPE_LABELS: Record<DomainType, string> = {
    report: 'Rapportpagina',
    app: 'Applicatie',
    api: 'Koppelingen',
};

/** De soorten die je in Instellingen kunt toevoegen. */
export const ADDABLE_DOMAIN_TYPES: readonly DomainType[] = ['report', 'app'];

/**
 * Waar een eigen domein staat op weg naar werken, in de volgorde waarin het gebeurt:
 * eigendom bewijzen, aan onze server toevoegen, het adres naar ons laten wijzen, beveiligen,
 * controleren. `manual` is het pad zonder Forge: het platform doet het met de hand.
 */
export const DOMAIN_SETUP_STEPS = [
    'verification',
    'manual',
    'server',
    'dns',
    'certificate',
    'checking',
    'active',
] as const;

export type DomainSetupStep = (typeof DOMAIN_SETUP_STEPS)[number];

/** De stappen die vanzelf verder gaan: zolang een domein daar staat, kijkt het scherm mee. */
const MOVING_SETUP_STEPS: readonly DomainSetupStep[] = ['server', 'certificate', 'checking'];

export interface DomainSetup {
    /** Het platform zet het domein zelf klaar (Forge gekoppeld, rapportdomein). */
    automatic: boolean;
    step: DomainSetupStep;
    /** Gestopt bij `step`; `reason` zegt waarom en wat je kunt doen. */
    failed: boolean;
    reason: string | null;
    /** Alleen bij `dns`: wanneer het platform de DNS weer bekijkt. */
    nextCheckAt: string | null;
    /** "Opnieuw proberen" doet nu iets. */
    canRetry: boolean;
    /** Het ene record dat het bedrijf zelf zet; `null` als er niets gevraagd wordt. */
    dnsRecord: { type: string; name: string; value: string } | null;
    /** Het platform is bezig en het scherm kijkt mee; niets van het bedrijf nodig. */
    inProgress: boolean;
}

export interface TenantDomain {
    uuid: string;
    hostname: string;
    type: DomainType;
    primary: boolean;
    verified: boolean;
    verifiedAt: string | null;
    sslStatusLabel: string;
    /** Wat er in DNS of op de website moet staan zolang het niet geverifieerd is. */
    verification: {
        /** Waar het record hoort: `_smartkeur.<domein>`, naast het CNAME-record van het domein zelf. */
        txtName: string;
        txtRecord: string;
        wellKnownPath: string;
        token: string;
    } | null;
    setup: DomainSetup;
}

export function toTenantDomain(resource: TenantDomainResource): TenantDomain {
    return {
        uuid: resource.uuid,
        hostname: resource.hostname,
        type: oneOf(DOMAIN_TYPES, resource.type) ?? 'report',
        primary: resource.is_primary,
        verified: resource.is_verified,
        verifiedAt: resource.verified_at,
        sslStatusLabel: resource.ssl_status_label,
        verification: resource.verification
            ? {
                  txtName: resource.verification.txt_name,
                  txtRecord: resource.verification.txt_record,
                  wellKnownPath: resource.verification.well_known_path,
                  token: resource.verification.token,
              }
            : null,
        setup: toDomainSetup(resource.setup),
    };
}

function toDomainSetup(resource: TenantDomainResource['setup']): DomainSetup {
    /* Een stap die de app nog niet kent, is voor het scherm "wordt klaargezet". */
    const step = oneOf(DOMAIN_SETUP_STEPS, resource.step) ?? 'manual';

    return {
        automatic: resource.automatic,
        step,
        failed: resource.failed,
        reason: resource.reason,
        nextCheckAt: resource.next_check_at,
        canRetry: resource.can_retry,
        dnsRecord: resource.dns_record,
        inProgress: resource.automatic && !resource.failed && MOVING_SETUP_STEPS.includes(step),
    };
}
